import type {
  EditingTab,
  EmploymentDetailsForm,
  EmploymentSaveMode,
} from "./profilePanel.contract";
import { getEmploymentFieldVisibility } from "../shared/staffEmploymentFieldVisibility.logic";

interface CanSaveEmploymentEditParams {
  editingTab: EditingTab;
  editedEmploymentDetails: EmploymentDetailsForm;
  isEndDateRequiredOnSave: boolean;
  employmentSaveMode: EmploymentSaveMode;
}

/** Returns true when the value contains at least one non-whitespace character. */
const hasNonEmptyValue = (value: string): boolean => value.trim().length > 0;

/** Validates that employee number only contains digits. */
const hasValidEmployeeNumber = (value: string): boolean =>
  /^\d+$/.test(value.trim());

/** Validates that value is a finite numeric string. */
const hasValidNumericValue = (value: string): boolean => {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return false;
  return Number.isFinite(Number(normalizedValue));
};

/** Validates phone number with optional leading + and country code digits. */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  const normalizedPhoneNumber = phoneNumber.trim();
  return /^\+?[1-9]\d{7,14}$/.test(normalizedPhoneNumber);
};

/** Returns whether employment form is valid enough to enable Save action. */
export const canSaveEmploymentEdit = (
  params: CanSaveEmploymentEditParams,
): boolean => {
  if (params.editingTab !== "employmentSalary") return true;
  const visibility = getEmploymentFieldVisibility(
    params.editedEmploymentDetails.employmentType,
  );
  if (!hasNonEmptyValue(params.editedEmploymentDetails.employmentType))
    return false;
  if (
    visibility.showEmployeeCategory &&
    !hasNonEmptyValue(params.editedEmploymentDetails.employeeCategory)
  ) {
    return false;
  }
  if (!hasValidEmployeeNumber(params.editedEmploymentDetails.employeeNumber))
    return false;
  if (!hasNonEmptyValue(params.editedEmploymentDetails.role)) return false;
  if (params.editedEmploymentDetails.startDate == null) return false;
  if (
    params.isEndDateRequiredOnSave &&
    params.editedEmploymentDetails.endDate == null
  ) {
    return false;
  }
  if (
    params.editedEmploymentDetails.endDate != null &&
    params.editedEmploymentDetails.endDate.isBefore(
      params.editedEmploymentDetails.startDate,
    )
  ) {
    return false;
  }
  if (params.employmentSaveMode !== "create") return true;
  if (
    visibility.showSalaryType &&
    !hasNonEmptyValue(params.editedEmploymentDetails.salaryType)
  ) {
    return false;
  }
  if (
    visibility.showSalary &&
    !hasValidNumericValue(params.editedEmploymentDetails.salary)
  ) {
    return false;
  }
  if (
    visibility.showPaymentType &&
    !hasNonEmptyValue(params.editedEmploymentDetails.paymentType)
  ) {
    return false;
  }
  if (
    visibility.showSalaryType &&
    params.editedEmploymentDetails.salaryType === "Monthly" &&
    !hasValidNumericValue(params.editedEmploymentDetails.workPercentage)
  ) {
    return false;
  }
  return true;
};
